import {
  Divider,
  Loader,
  Stack,
  Tab,
  TabPanel,
  TabPanels,
  Tabs,
  TabsProvider,
} from 'braid-design-system';

import { usePermissions } from '#src/hooks/auth.tsx';
import type {
  WebhookDetailsQuery,
  WebhookDetailsQueryVariables,
} from '#src/types/generated/graphql.ts';

import { useQuery } from '@apollo/client/react';
import { createContext, useContext } from 'react';
import { ErrorNotice } from '#src/components/ErrorNotice/ErrorNotice.tsx';
import { EventList } from './components/EventList/EventList.tsx';
import { ReplayWebhook } from './components/ReplayWebhook/ReplayWebhook.tsx';
import { SubscriptionDetail } from './components/SubscriptionDetail/SubscriptionDetail.tsx';
import { SubscriptionHealthCard } from './components/SubscriptionHealthCard/SubscriptionHealthCard.tsx';
import { SubscriptionReplayList } from './components/SubscriptionReplayList/SubscriptionReplayList.tsx';
import { WebhookRequestList } from './components/WebhookRequestList/WebhookRequestList.tsx';
import { GET_WEBHOOK_DETAILS } from './queries.ts';

type UseQueryResult = ReturnType<
  typeof useQuery<WebhookDetailsQuery, WebhookDetailsQueryVariables>
>;

const Context = createContext<{
  queryResult: UseQueryResult;
} | null>(null);

export const ViewSubscriptionPanelProvider = ({
  subscriptionId,
  children,
}: {
  subscriptionId: string;
  children: React.ReactNode;
}) => {
  const queryResult = useQuery<
    WebhookDetailsQuery,
    WebhookDetailsQueryVariables
  >(GET_WEBHOOK_DETAILS, {
    fetchPolicy: 'cache-first',
    variables: { id: subscriptionId },
  });

  return (
    <Context.Provider value={{ queryResult }}>{children}</Context.Provider>
  );
};

export const ViewSubscriptionPanel = ({
  subscriptionId,
}: {
  subscriptionId: string;
}) => {
  const context = useContext(Context);
  if (!context) {
    throw new Error(
      'ViewSubscriptionPanel must be used within a ViewSubscriptionPanelProvider',
    );
  }

  const {
    queryResult: { loading, error, data },
  } = context;

  const { permissions } = usePermissions();

  if (loading) {
    return <Loader />;
  }

  if (error || !data) {
    return (
      <ErrorNotice
        description={error?.message ?? 'An unknown error occurred'}
        title="We couldn’t load your subscription."
      />
    );
  }

  if (!data.webhookSubscription) {
    return (
      <ErrorNotice
        description="The subscription you’re looking for doesn’t exist."
        title="Subscription not found"
      />
    );
  }

  return (
    <Stack space="large">
      <SubscriptionDetail subscription={data.webhookSubscription} />

      <Divider />

      <SubscriptionHealthCard subscriptionId={subscriptionId} />

      {permissions.includes('mutate:webhooks') && (
        <>
          <Divider />

          <ReplayWebhook
            subscriptionId={subscriptionId}
            subscriptionHirerId={data.webhookSubscription.hirerId?.value}
            subscriptionHirerName={data.webhookSubscription.hirer?.name}
          />
        </>
      )}

      <TabsProvider id="webhook-subscription-tabs">
        <Tabs
          align="center"
          divider="full"
          label="Webhook subscription tabs"
          reserveHitArea
        >
          <Tab>Events</Tab>
          <Tab>Requests</Tab>
          <Tab>Replays</Tab>
        </Tabs>

        <TabPanels>
          <TabPanel>
            <EventList subscriptionId={subscriptionId} />
          </TabPanel>
          <TabPanel>
            <WebhookRequestList subscriptionId={subscriptionId} />
          </TabPanel>
          <TabPanel>
            <SubscriptionReplayList subscriptionId={subscriptionId} />
          </TabPanel>
        </TabPanels>
      </TabsProvider>
    </Stack>
  );
};
